<template>
  <div>
    <div class="container">
      <div class="relative isolate z-10">
        <LazyUiBreadcrumbs />
      </div>
    </div>
    <BlocksBlockRichText
      v-if="blogRichModuleData"
      :data="blogRichModuleData as RichTextModuleData"
    />
    <div class="container">
      <PagesBlogLatestBlog v-if="latestBlog" :blog="latestBlog" />
      <PagesBlogLatestBlogs
        v-if="latestBlogs?.length"
        :latest-blogs="latestBlogs"
      />
      <hr id="list" />
      <PagesBlogCategories
        v-if="blogCategoriesData?.length"
        :categories-data="blogCategoriesData"
      />
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { RichTextModuleData } from "ecom-types";

const route = useRoute();
const { t } = useI18n();
const { $api } = useNuxtApp();
const { setBreadcrumbs } = useBreadcrumbs();
const generalStore = useGeneralStore();
const { visitEvent } = useEvents();
const i18nHead = useLocaleHead({
  addSeoAttributes: {
    canonicalQueries: ["page"],
  },
});

const filter = computed(() => {
  return {
    page: 1,
    count: 3,
  };
});

const debouncedFilter = refDebounced(filter, 300);

const { data: apiData } = await useAsyncData("blog-data", async () => {
  const [categoriesData, blogRichModule, pageData, blogsResp] =
    await Promise.all([
      $api.blog.getBlogCategories(),
      $api.blocks.getBlock("blog-rich-module"),
      $api.pages.getPage("blog"),
      $api.blog.getPosts(objectToQuery(debouncedFilter.value)),
    ]);

  return {
    categoriesData,
    blogRichModule,
    pageData,
    blogsResp,
  };
});

const blogRichModuleData = computed(() => {
  return apiData.value?.blogRichModule?.data?.currentTranslation?.content;
});

const blogCategoriesData = computed(() => {
  return apiData.value?.categoriesData?.data;
});

const blogPageData = computed(() => {
  return apiData.value?.pageData?.data;
});

const latestBlogs = computed(() => apiData.value?.blogsResp?.data?.slice(1, 3));
const latestBlog = computed(
  () => apiData.value?.blogsResp?.data?.slice(0, 1)?.[0] || null,
);
const activeBlogSection = computed(() =>
  blogCategoriesData.value?.find((el) => el.slug === route.params?.section),
);

const blogPageSchemaName = computed(() => {
  return (
    activeBlogSection.value?.currentTranslation?.title ||
    blogPageData.value?.currentTranslation?.title ||
    ""
  );
});

watchEffect(() => {
  const blogSectionSlug = route.params.section;
  const postSlug = route.params.slug; // need because component created before route change
  if (!activeBlogSection.value && blogSectionSlug && !postSlug) {
    throw createError({
      statusCode: 404,
      fatal: true,
    });
  }
});

setBreadcrumbs([
  { text: t("home"), href: "/", disabled: false },
  {
    text: t("blog"),
    disabled: true,
    href: "/blog",
  },
]);

const { seoTitle, seoDescription } = usePageSeo();
const { seoBlogTitle, seoBlogDescription } = useBlogPageSeo();

useHead({
  meta: [
    ...metaGenerator({
      title:
        seoBlogTitle.value ||
        blogPageData.value?.currentTranslation?.meta_title ||
        seoTitle.value,
      description:
        seoBlogDescription.value ||
        blogPageData.value?.currentTranslation?.meta_description ||
        seoDescription.value,
      image:
        activeBlogSection.value?.medias?.find(
          (el) => el.position === "open-graph",
        )?.file?.url ||
        blogPageData.value?.medias?.find((el) => el.position === "open-graph")
          ?.file?.url ||
        "",
      keywords:
        activeBlogSection.value?.currentTranslation?.meta_keywords ||
        blogPageData.value?.currentTranslation?.meta_keywords ||
        "",
      url: generalStore.bootstrap?.settings?.front_domain + route.path,
    }),
    ...(i18nHead.value.meta || []),
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
});

useSeoMeta({
  title: () => seoTitle.value,
  ogTitle: () => seoTitle.value,
  description: () =>
    seoBlogDescription.value ||
    blogPageData.value?.currentTranslation?.meta_description ||
    seoDescription.value,
  ogDescription: () =>
    seoBlogDescription.value ||
    blogPageData.value?.currentTranslation?.meta_description ||
    seoDescription.value,
});

onMounted(() => {
  visitEvent(blogPageData?.value?.is_plp);
});

function usePageSeo() {
  const seoTitle = computed(() => {
    return seoBlogTitle.value;
  });

  const seoDescription = computed(() => {
    return seoBlogDescription.value;
  });
  return {
    seoTitle,
    seoDescription,
  };
}

function useBlogPageSeo() {
  const paginationPage = computed(() => {
    return route.query.page || 1;
  });

  const seoBlogTitle = computed(() => {
    const seoTitle =
      activeBlogSection.value?.currentTranslation?.meta_title ||
      blogPageData.value?.currentTranslation?.meta_title;
    if (seoTitle) {
      return `${seoTitle}: ${t("page")} №${paginationPage.value} ${generalStore.settings?.company_name ? ` | ${generalStore.settings.company_name}` : ""}`;
    }
    let title =
      activeBlogSection.value?.currentTranslation?.title ||
      blogPageData.value?.currentTranslation?.title ||
      "";

    if (generalStore.settings?.company_name && title) {
      title = `${title} | ${t("page")} №${paginationPage.value} | ${generalStore.settings.company_name}`;
    } else if (generalStore.settings?.company_name) {
      title = `${t("page")} №${paginationPage.value} | ${generalStore.settings.company_name}`;
    }

    return title;
  });

  const seoBlogDescription = computed(() => {
    const seoDescription =
      activeBlogSection.value?.currentTranslation?.meta_description ||
      blogPageData.value?.currentTranslation?.meta_description ||
      "";
    if (seoDescription) {
      return seoDescription;
    }

    const description =
      activeBlogSection.value?.currentTranslation?.description ||
      blogPageData.value?.currentTranslation?.description ||
      "";
    if (!description) {
      return "";
    }
    return `${description} ${t("pageMetaDescription")}`;
  });
  return {
    seoBlogTitle,
    seoBlogDescription,
  };
}
provide(blogPageSchemaNameKey, blogPageSchemaName);
</script>
